import React from 'react';
import { Folder } from 'react-feather';
import { Layers } from 'react-feather';
import { sidebysides, simpleInlineStyles } from '@threeskye/editor-component-slate';
import { ReactEditor } from 'slate-react';
import { Transforms, Node as SlateNode, Path as SlatePath, Editor as SlateEditor, Point, Range } from 'slate';
import { removeFootnote } from '@threeskye/editor-component-slate/dist/menu/Utils.js';
import { listsOnKeyDown, withLists, getListDepth, resolveListStyleByDepth } from './listsPlugin';
import { toastSuccess } from '../components/popups/Toast.js';
import { getElementsByAttribute, getElementsByType, checkIfItHasSuperscript, getNewFWposition, getTextWidth, getNewContentPosition, findNodeAtHeight } from '../Utils/SlateUtils.js';
import TableRowStyleMenuItem from '@threeskye/editor-component-slate/dist/engine/context/table/TableRowStyleMenuItem';
import TableColumnStyleMenuItem from '@threeskye/editor-component-slate/dist/engine/context/table/TableColumnStyleMenuItem';
import EditorSubMenu from '@threeskye/editor-component-slate/dist/engine/context/EditorSubMenu';
import withPageBreaks from '../withPageBreaks';
import TableOfContents from '../contents/TableOfContents.js';
import FlowingPageFootnoteWrapper from '../contents/FlowingPageFootNoteWrapper.js';
import { pxlsToPts } from './pageLayoutMath';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function pasteToAll(portfoliosState, remote, content, templateName, dataName) {
	try {
		const tickers = (portfoliosState || [])
			.map(ticker => ticker && ticker.fundNumber)
			.filter(Boolean);
		if (tickers.length === 0) return;

		const body = {
			tickers,
			data: { [dataName]: JSON.stringify(content) }
		};

		return remote.put("/savev2-bulk/" + templateName, body).then(response => {
			if (response && response.success) {
				toastSuccess("Portfolios updated");
			}
		}).catch(err => {
			console.log("Catch response, ", err);
		});
	} catch (err) {
		toastSuccess("Falied to update portfolios", err);
	}
}

/**
 * Build the Slate editor's `extensions` prop value from a string array of extension
 * names declared on a template configuration. Returns an object shaped like
 * `{ pasteToAll: { show, onClick, length }, ... }` consumable by the editor.
 *
 * Used by FlowingPage's buildEditorAddins, and by EditableText for in-place
 * editor blocks (where `insertPageBreak` is not applicable — pass undefined).
 */
export function buildSlateExtensions({
	extensionsConfig,
	getPortfoliosState,
	getPortfoliosGroupedState,
	getCurrentFundNumber,
	getPasteGroupings,
	getAllFunds,
	pasteGroupName,
	remote,
	getContent,
	templateName,
	dataName,
	keepInsertOpen,
	setKeepInsertOpen,
	onInsertPageBreak,
	groups, // TODO: pass live groups state from parent once API endpoint exists
}) {
	const slateExtensions = {};
	const extraButtons = [];
	if (!extensionsConfig) return slateExtensions;

	// Read once at build time for the button count + filtered options. Click handlers
	// re-read via the getter so the action uses live state even if Page.js cached
	// stale props at the time this component was first mounted.
	const portfoliosState = getPortfoliosState ? getPortfoliosState() : undefined;
	const portfoliosGroupedState = getPortfoliosGroupedState ? getPortfoliosGroupedState() : undefined;

	if (extensionsConfig.includes("PASTE_TO_ALL")) {
		slateExtensions.pasteToAll = {
			show: true,
			onClick: () => {
				const liveState = getPortfoliosState ? getPortfoliosState() : portfoliosState;
				pasteToAll(liveState, remote, getContent(), templateName, dataName);
			},
			length: portfoliosState ? portfoliosState.length : 0
		};
	}
	if (extensionsConfig.includes("PASTE_TO_GROUP") && pasteGroupName) {
		// `groupings` is List<FundGroupingDTO> from /funds/funds/groupings:
		// [{ groupingId, groupingName, groups: [{ groupId, groupName, funds: [fundId, ...] }] }, ...]
		const groupings = getPasteGroupings ? getPasteGroupings() : undefined;
		const allFunds = getAllFunds ? getAllFunds() : undefined;
		const currentFundNumber = getCurrentFundNumber ? getCurrentFundNumber() : undefined;
		const grouping = groupings && groupings.find(g => g.groupingName === pasteGroupName);
		const groups = grouping && grouping.groups;

		if (groups && groups.length > 0) {
			const fundsById = {};
			(allFunds || []).forEach(f => { fundsById[f.id] = f; });

			const currentFund = currentFundNumber && (allFunds || []).find(f => f.fundNumber === currentFundNumber);
			const currentGroup = currentFund && groups.find(g => (g.funds || []).includes(currentFund.id));

			extraButtons.push({
				key: 'pasteToGroup',
				type: 'multiSelectPopup',
				show: true,
				icon: <Layers size={20} color="#1A2038" className="horiz-context-menu-icon" />,
				tooltip: 'Replicate to Group',
				setKeepInsertOpen,
				options: groups.map(g => {
					const count = (g.funds || []).length;
					const isCurrent = currentGroup && currentGroup.groupId === g.groupId;
					const label = `${g.groupName} (${count})${isCurrent ? ' — current' : ''}`;
					return { label, ...g };
				}),
				onConfirm: (selectedGroups) => {
					const fundsToPaste = [];
					const seen = new Set();
					selectedGroups.forEach(g => {
						(g.funds || []).forEach(fundId => {
							if (seen.has(fundId)) return;
							seen.add(fundId);
							const f = fundsById[fundId];
							if (f && f.fundNumber) fundsToPaste.push(f);
						});
					});
					pasteToAll(fundsToPaste, remote, getContent(), templateName, dataName);
				},
			});
		}
	}
	if (extensionsConfig.includes("PASTE_TO_SELECTED")) {
		const portfolioOptions = portfoliosState ? portfoliosState.filter(ticker => {
			if (portfoliosGroupedState && portfoliosGroupedState) {
				return ticker.offerName && portfoliosGroupedState.includes(ticker.offerName);
			} else return true;
		}) : [];

		// TODO: groups will come from API once endpoint exists — passed in as prop
		const groupOptions = (groups || []).map(g => ({
			label: <><Folder size={13} style={{ color: '#0D8EFD', marginRight: 5, verticalAlign: 'middle', flexShrink: 0 }} />{g.name}</>,
			_isGroup: true,
			_fundNumbers: g.fundNumbers
		}));
		const individualOptions = portfolioOptions.length > 0 ? portfolioOptions.map(ticker => ({ label: ticker.fundName, ...ticker })) : [];
		const allOptions = [...groupOptions, ...individualOptions];

		slateExtensions.pasteToSpecific = {
			show: true,
			onClick: (selected) => {
				// Expand group entries into their individual portfolio objects
				const expanded = [];
				selected.forEach(opt => {
					if (opt._isGroup) {
						(opt._fundNumbers || []).forEach(fn => {
							const p = (portfoliosState || []).find(t => t.fundNumber === fn);
							if (p && !expanded.some(e => e.fundNumber === fn)) expanded.push(p);
						});
					} else {
						if (!expanded.some(e => e.fundNumber === opt.fundNumber)) expanded.push(opt);
					}
				});
				pasteToAll(expanded.length > 0 ? expanded : selected, remote, getContent(), templateName, dataName);
			},
			keepInsertOpen,
			setKeepInsertOpen,
			options: allOptions
		};
	}
	if (extensionsConfig.includes("INSERT_TABLE")) {
		slateExtensions.insertTable = {
			show: true,
			keepInsertOpen,
			setKeepInsertOpen,
		};
	}
	if (extensionsConfig.includes("INSERT_TABLE_OF_CONTENTS")) {
		slateExtensions.insertTableOfContents = {
			show: true,
		};
	}
	if (extensionsConfig.includes("INSERT_PAGE_BREAK") && onInsertPageBreak) {
		slateExtensions.insertPageBreak = {
			show: true,
			onClick: onInsertPageBreak,
		};
	}
	if (extensionsConfig.includes("INSERT_FOOTNOTE")) {
		slateExtensions.insertFootnote = {
			show: true,
		};
	}
	if (extraButtons.length > 0) {
		slateExtensions.extraButtons = extraButtons;
	}
	return slateExtensions;
}

function withTOC(editor) {
	const { isVoid } = editor;
	editor.isVoid = element => {
		return element.type === "toc-label" ? true : isVoid(element);
	};
	return editor;
}

function insertPageBreak(selection, state, data, setState, heightChange, height) {
	const elementsWithLongerPath = ['numbered-list', 'bulleted-list'];
	const ref = state.divRef;
	const nodePath = selection.anchor.path;
	const node = state.editor.children[nodePath[0]];
	const actualNoode = elementsWithLongerPath.includes(node.type) ? state.editor.children[nodePath[0]].children[nodePath[1]] : node;
	const domNode = ReactEditor.toDOMNode(state.editor, actualNoode);
	const refTop = ref.current.getBoundingClientRect().top;

	// Use the cursor's Y position (via the DOM selection range), not the
	// block's top. This places the break at the line where the cursor is,
	// allowing mid-paragraph manual breaks.
	const cursorOffset = selection.anchor.offset;
	let breakY;
	let breakCharOffset = 0;
	try {
		const domRange = ReactEditor.toDOMRange(state.editor, selection);
		const cursorRect = domRange.getBoundingClientRect();
		// cursorRect may be zero-width for a collapsed selection — y is valid
		breakY = cursorRect.y;
		breakCharOffset = cursorOffset;
	} catch (e) {
		// Fallback to block top if DOM range fails
		breakY = domNode.getBoundingClientRect().y;
		breakCharOffset = 0;
	}
	const breakPosition = pxlsToPts(breakY - refTop);

	const theBreak = {
		at: elementsWithLongerPath.includes(node.type) ? [nodePath[0], nodePath[1]] : [nodePath[0]],
		id: Math.ceil(Math.random() * 100000),
		manual: true,
		offset: breakCharOffset,
		pageBreakId: Math.round(1 + Math.random() * 10000000),
		pos: breakPosition
	};

	state.editor.addPageBreak(theBreak, theBreak.at);

	const sortedManual = state.manualPageBreaks.concat([theBreak]).sort((a, b) => a.pos - b.pos);

	setState({ manualPageBreaks: sortedManual }, () => {
		heightChange(height, state, sortedManual);
		const dataName = data.templateName || "MainBody";
		setTimeout(() => {
			const allBoundaries = state.pageBreaks.concat(sortedManual);
			data.updateLocalData(dataName + 'PageBreaks', JSON.stringify(allBoundaries, (k, v) => { if (k === 'ref') { return undefined; } return v; }));
		}, 500);
	});
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Build the editor addins and options object for FlowingPage.
 *
 * @param {object} config    - { editableStyles, tableStyles, tableStyleRef, allBoundaries, allowFullWidthTable }
 * @param {object} props     - component props (data, styles, flowingConfig, portfoliosState, portfoliosGroupedState, remote)
 * @param {object} callbacks - { setState, setToolbar, heightChange, height, state }
 * @returns {{ addins, options, myStyles }}
 */
export function buildEditorAddins(config, props, callbacks) {
	const { editableStyles, tableStyles, tableStyleRef, allBoundaries, allowFullWidthTable } = config;
	const { setState, setToolbar, heightChange, height, state } = callbacks;

	const myStyles = editableStyles;
	Object.assign(myStyles, tableStyles);
	const options = {};

	if (allowFullWidthTable) {
		const margins = props.flowingConfig.margins;
		const size = props.pageSize;
		const marginLeft = props.flowingConfig.fullWidthTableMargins.left;
		const marginRight = props.flowingConfig.fullWidthTableMargins.right;

		myStyles["page-width"] = {
			"position": "relative",
			"left": (marginLeft - margins.left) + 'pt',
			"width": (size.width - marginLeft - marginRight) + 'pt',
			"background": "white"
		};
		myStyles["full-width"] = {
			"position": "relative",
			"left": (marginLeft - margins.left) + 'pt',
			"width": (size.width - marginLeft - marginRight) + 'pt',
			"background": "white"
		};
		myStyles["table-border"] = {
			"border": "1px solid #004271"
		};
		myStyles["full-width-table-border"] = {
			"position": "relative",
			"left": (marginLeft - margins.left) + 'pt',
			"width": (size.width - marginLeft - marginRight) + 'pt',
			"background": "white",
			"border": "1px solid #004271"
		};
		myStyles["image-border"] = {
			"border-top": "1.25pt solid #C1CBDE",
			"border-bottom": "1.25pt solid #C1CBDE",
			"padding-top": "5pt",
			"margin-bottom": "3pt"
		};
		myStyles["text-width-table-border"] = {
			"border": "1px solid #004271"
		};
		myStyles["text-width-image-border"] = {
			"border-top": "1.25pt solid #C1CBDE",
			"border-bottom": "1.25pt solid #C1CBDE",
			"padding-top": "5pt",
			"padding-bottom": "5pt"
		};

		["tableStyleOptions"].forEach(key => {
			options[key] = [
				{ label: "Text Width", key: "text-width" },
				{ label: "Page Width", key: "full-width" },
				{ label: "Text Width (with Border)", key: "text-width-table-border" },
				{ label: "Page Width (with Border)", key: "full-width-table-border" }
			];
		});

		["imageStyleOptions", "sideBySideStyleOptions"].forEach(key => {
			options[key] = [
				{ label: "Text Width", key: "text-width" },
				{ label: "Page Width", key: "full-width" },
				{ label: "Text Width (with Border)", key: "text-width-image-border" },
				{ label: "Page Width (with Border)", key: "full-width-image-border" }
			];
		});
	} else {
		if (myStyles["tableBorder"]) {
			myStyles["table-border"] = {
				"border": `${myStyles["tableBorder"].borderBottomWidth} ${myStyles["tableBorder"].borderBottomStyle} ${myStyles["tableBorder"].borderBottomColor}`
			};
		}
	}

	if (props.flowingConfig.rowStyleOptions) {
		options.rowStyleOptions = props.flowingConfig.rowStyleOptions;
	}
	if (props.flowingConfig.columnStyleOptions) {
		options.columnStyleOptions = props.flowingConfig.columnStyleOptions;
	}

	const rowStyles = options.rowStyleOptions.map((option, idx) => <TableRowStyleMenuItem key={idx} styleName={option.key} text={option.label} showPreview={true} />);
	const columStyles = options.columnStyleOptions.map((option, idx) => <TableColumnStyleMenuItem key={idx} styleName={option.key} text={option.label} showPreview={true} />);

	const menuItems = [
		<EditorSubMenu title='Row styles'>{rowStyles}</EditorSubMenu>,
		<EditorSubMenu title='Column styles'>{columStyles}</EditorSubMenu>,
	];

	const tableOptions = {
		initialFooter: true,
		menuItems: menuItems,
		styleRef: tableStyleRef,
		styleInterpreter: style => {
			if (style === 'page-width') return myStyles["full-width"];
			if (style === 'bordered-table') return myStyles["table-border"];
			if (style === 'page-width-with-border') {
				return Object.assign({}, myStyles["full-width"], myStyles["image-border"]);
			}
			if (style === 'text-width-with-border') return myStyles["image-border"];
			for (const { label, key } of options.rowStyleOptions) {
				if (style === key) return myStyles[key];
			}
			for (const { label, key } of options.columnStyleOptions) {
				if (style === key) return myStyles[key];
			}
		},
		hoverOptions: allowFullWidthTable ? ["border", "width"] : ["border"]
	};

	const headings = {
		install: editor => {
			return {
				getRenderer: (type) => {
					if (type === "heading-one")
						return ({ attributes, children }) => {
							const updatechildren = children.map((child) => {
								if (child?.props?.text?.superscript) {
									return (<sup style={{ fontSize: '66%', verticalAlign: 'top' }}>{child}</sup>);
								} else return child;
							});
							return (<h1 {...attributes}>{updatechildren}</h1>);
						};
					else if (type === "heading-two")
						return ({ attributes, children }) => {
							const updatechildren = children.map((child) => {
								if (child?.props?.text?.superscript) {
									return (<sup style={{ fontSize: '66%', verticalAlign: 'top' }}>{child}</sup>);
								} else return child;
							});
							return (<h2 {...attributes}>{updatechildren}</h2>);
						};
					else if (type === "heading-three")
						return ({ attributes, children }) => {
							const updatechildren = children.map((child) => {
								if (child?.props?.text?.superscript) {
									return (<sup style={{ fontSize: '66%', verticalAlign: 'top' }}>{child}</sup>);
								} else return child;
							});
							return (<h3 {...attributes}>{updatechildren}</h3>);
						};
					else
						return null;
				},
				editor: editor
			};
		}
	};

	const lists = {
		install: editor => {
			withLists(editor);
			return {
				getRenderer: (type, editor) => {
					if (type === "list-item")
						return ({ attributes, children, element }) => {
							const path = ReactEditor.findPath(editor, element);
							const parentPath = SlatePath.parent(path);
							let grandpaNode = null;
							let isCellChild = false;
							try {
								const grandpaPath = SlatePath.parent(parentPath);
								grandpaNode = SlateNode.get(editor, grandpaPath);
								isCellChild = grandpaNode && grandpaNode.type === 'table-cell';
							} catch (e) {
								isCellChild = false;
							}
							const liName = props.flowingConfig && props.flowingConfig.styles && props.flowingConfig.styles.li;
							const liStyleObj = liName && props.styles && props.styles[liName];
							const liInlineStyle = liStyleObj && props.data && props.data.getStyle ? props.data.getStyle(liStyleObj) : null;
							const cellStyle = isCellChild
								? { /*margin: 0,*/ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', textAlign: 'inherit'/*lineHeight: 'inherit'*/ } : null;
							const style = cellStyle || liInlineStyle ? { ...liInlineStyle, ...cellStyle } : null;

							const updatechildren = children.map((child) => {
								if (child.props.text && child.props.text.url !== undefined && child.props.text.url !== null) {
									const url = child.props.text.url;
									const href = (url.includes('http://') || url.includes('https://')) ? url : 'http://' + url;
									const colour = child.props.text.color;
									const decoration = child.props.text.underline ? 'underline' : 'none';
									const text = child.props.text.text;
									const linkStyles = { fontWeight: 600, cursor: 'pointer', color: colour || '#A9754E', textDecoration: decoration };
									return (
										<a target='_blank' href={href} style={linkStyles} onClick={(e) => setToolbar({ type: 'link-tooltip', text: text, href: href })} {...attributes}>
											{child}
										</a>
									);
								} else if (child?.props?.text?.superscript) {
									return (<sup style={{ fontSize: '66%', verticalAlign: 'top' }}>{child}</sup>);
								} else return child;
							});
							return <li {...attributes} style={style}>{updatechildren}</li>;
						};
					else if (type === "numbered-list")
						return ({ attributes, children, element }) => {
							const path = ReactEditor.findPath(editor, element);
							const parentPath = SlatePath.parent(path);
							const parentNode = SlateNode.get(editor, parentPath);
							const isCellChild = parentNode && parentNode.type === 'table-cell';
							const cellStyle = isCellChild ? { margin: 0, color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', /*lineHeight: 'inherit'*/ } : null;
							const depthStyle = resolveListStyleByDepth(getListDepth(editor, path), 'numbered-list', { config: props.flowingConfig, styles: props.styles, data: props.data });
							const style = cellStyle || depthStyle ? { ...cellStyle, ...depthStyle } : null;
							return <ol {...attributes} style={style}>{children}</ol>;
						};
					else if (type === "bulleted-list")
						return ({ attributes, children, element }) => {
							const path = ReactEditor.findPath(editor, element);
							const parentPath = SlatePath.parent(path);
							const parentNode = SlateNode.get(editor, parentPath);
							const isCellChild = parentNode && parentNode.type === 'table-cell';
							const cellStyle = isCellChild ? { margin: 0, color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', /*lineHeight: 'inherit'*/ } : null;
							const depthStyle = resolveListStyleByDepth(getListDepth(editor, path), 'bulleted-list', { config: props.flowingConfig, styles: props.styles, data: props.data });
							const style = cellStyle || depthStyle ? { ...cellStyle, ...depthStyle } : null;
							return <ul {...attributes} style={style}>{children}</ul>;
						};
					else
						return null;
				},
				onKeyDown: (event) => {
					listsOnKeyDown(editor, event);
				},
				editor: editor
			};
		}
	};

	const editorRef = {
		install: editor => {
			setState({ editor });
			return { editor: editor };
		}
	};

	const pageBreaksAddin = {
		install: withPageBreaks
	};

	const imageOptions = {
		menuItems: allowFullWidthTable ? ["width", "border"] : ["border"],
		styleInterpreter: style => {
			if (style === 'page-width') return myStyles["full-width"];
			if (style === 'page-width-with-border') {
				return Object.assign({}, myStyles["full-width"], myStyles["image-border"]);
			}
			if (style === 'text-width-with-border') return myStyles["image-border"];
			return null;
		},
		contextMenu: {
			horizontal: true,
			iconButton: true
		},
		contextMenuOrder: [0, 1, 2, 3, "divider", "border", "divider", "header", "footer", "divider", "createSBS", "breakSBS", "divider", "delete"]
	};

	const sbsOptions = {
		styleInterpreter: style => {
			if (style && style.includes("page-width") && style.includes("with-border") && style.includes("with-gap")) {
				const joined = Object.assign({}, myStyles["full-width"], myStyles["image-border"], myStyles["with-gap"]);
				return { ...joined, gap: myStyles["sbsGap"] ? myStyles["sbsGap"].fontSize : 12 };
			} else if (style && style.includes("page-width") && style.includes("with-border")) {
				return Object.assign({}, myStyles["full-width"], myStyles["image-border"]);
			} else if (style && style.includes("page-width") && style.includes("with-gap")) {
				return { ...myStyles["page-width"], gap: myStyles["sbsGap"] ? myStyles["sbsGap"].fontSize : 12 };
			} else if (style && style.includes("with-gap") && style.includes("with-border")) {
				return { ...myStyles["image-border"], gap: myStyles["sbsGap"] ? myStyles["sbsGap"].fontSize : 12 };
			} else if (style && style.includes('text-width') && style.includes('with-border')) {
				return myStyles["image-border"];
			} else if (style === "with-border") {
				return myStyles["image-border"];
			} else if (style === "page-width") {
				return myStyles["full-width"];
			} else if (style === "with-gap") {
				return { gap: myStyles["sbsGap"] ? myStyles["sbsGap"].fontSize : 12 };
			}
		}
	};

	const tableOfContents = {
		install: editor => {
			return {
				getRenderer: (type, editor) => {
					if (type === "table-of-contents")
						return ({ attributes, children, element }) => {
							const id = element.id;
							let location = [];
							for (const [node, path] of SlateNode.descendants(editor)) {
								if (node.id && node.id === id) {
									location.push(path[0]);
								}
							}
							return (
								<TableOfContents
									attributes={attributes}
									children={children}
									editor={editor}
									data={props.data}
									pageBreaks={state.pageBreaksRef ? state.pageBreaksRef.current : state.pageBreaks}
									manualPageBreaks={state.manualPageBreaksRef ? state.manualPageBreaksRef.current : state.manualPageBreaks}
									styles={props.styles}
									flowStyles={props.flowingConfig.styles}
									element={element}
									elementLocation={location}
									startingPageNumber={props.flowingConfig?.startingPageNumber}
								/>
							);
						};
					else if (type === "placeholder")
						return ({ attributes, children }) => {
							return (<p {...attributes} style={{ display: 'none' }} contentEditable={false}>{children}</p>);
						};
					else
						return null;
				},
				editor: withTOC(editor)
			};
		}
	};

	const pageFootnoteWrapper = {
		install: editor => {
			return {
				getRenderer: (type, editor) => {
					if (type === "footnote-wrapper")
						return ({ attributes, children, ...fwProps }) => {
							const path = ReactEditor.findPath(editor, fwProps.element);
							return (
								<FlowingPageFootnoteWrapper
									attributes={attributes}
									children={children}
									editor={editor}
									data={props.data}
									element={fwProps.element}
									elementLocation={path}
									allBoundaries={allBoundaries}
									flowStyles={props.flowingConfig.styles}
									styles={props.styles}
								/>
							);
						};
					else if (type === "footnote-content")
						return ({ attributes, children, ...fwProps }) => {
							const superscript = fwProps.element.superscriptNumber;
							const flowStyles = props.flowingConfig.styles;
							const styles = props.styles;
							const styleName = flowStyles['footnoteContent'];
							const fnContentStyle = styles[styleName] ? styles[styleName] : {};
							const fncStyle = props.data.getStyle(fnContentStyle);

							const updatechildren = children.map((child) => {
								if (child.props.text && child.props.text.url !== undefined && child.props.text.url !== null) {
									const url = child.props.text.url;
									const href = (url.includes('http://') || url.includes('https://')) ? url : 'http://' + url;
									const colour = child.props.text.color;
									const decoration = child.props.text.underline ? 'underline' : 'none';
									const text = child.props.text.text;
									const linkStyles = { fontWeight: 600, cursor: 'pointer', color: colour || '#A9754E', textDecoration: decoration };
									return (
										<a target='_blank' href={href} style={linkStyles} onClick={(e) => setToolbar({ type: 'link-tooltip', text: text, href: href })} {...attributes}>
											{child}
										</a>
									);
								} else return child;
							});


							return (
								<p {...attributes} style={fncStyle}>
									<sup
										suppressContentEditableWarning={true}
										contentEditable={false}
										style={{ userSelect: 'none', pointerEvents: 'none', fontSize: '66%', verticalAlign: 'top' }}
										onMouseDown={(e) => {
											e.preventDefault();
											e.stopPropagation();
											Transforms.deselect(editor);
										}}
										onFocus={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
									>
										{superscript}
									</sup>
									{updatechildren}
								</p>
							);
						};
					else
						return null;
				},
				onKeyDown: (event) => {
					const { selection } = editor;
					if (!selection || !editor.selection) return;

					const anchorNodeEl = window.getSelection().anchorNode;
					const anchorNode = ReactEditor.toSlateNode(editor, anchorNodeEl);
					const anchorNodePath = ReactEditor.findPath(editor, anchorNode);

					const charactersNotAllowedInsideSuperscript = event.key.length === 1 && /^[\w\s\p{P}]$/u.test(event.key);
					const isUndo = (event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey;
					const isRedo = (event.metaKey || event.ctrlKey) && event.key === 'z' && event.shiftKey;
					const isUndoRedo = isUndo || isRedo;

					if (anchorNode && anchorNode.superscript && charactersNotAllowedInsideSuperscript) {
						console.log("# charactersNotAllowedInsideSuperscript", charactersNotAllowedInsideSuperscript);
						const sameNode = selection && Point.equals(selection.anchor, selection.focus);
						const isCollapsed = sameNode && selection && Range.isCollapsed(selection);
						const anchor = !isCollapsed && selection ? Point.isBefore(selection.anchor, selection.focus) ? selection.anchor : selection.focus : isCollapsed ? selection.anchor : null;
						const focus = !isCollapsed && selection ? Point.isBefore(selection.anchor, selection.focus) ? selection.focus : selection.anchor : isCollapsed ? selection.focus : null;
						const isStart = anchor && Point.equals(anchor, SlateEditor.start(editor, anchor.path));
						const isEnd = focus && Point.equals(focus, SlateEditor.end(editor, focus.path));

						if (charactersNotAllowedInsideSuperscript && !isUndoRedo) {
							console.log("#@# isStart", isStart, "isEnd", isEnd, "isCollapsed", isCollapsed);
							event.preventDefault();
							if (isStart && isCollapsed) {
								Transforms.insertNodes(editor, { text: event.key }, { at: selection.focus });
							}
							if (isEnd && isCollapsed) {
								Transforms.insertNodes(editor, { text: event.key }, { at: selection.anchor });
							}
							if (!isCollapsed && isStart && isEnd) {
								Transforms.insertNodes(editor, { text: event.key }, { ...selection });
							}
							return;
						}
					}

					if (event.key === 'Backspace' || event.key === 'Delete') {
						const anchor = Point.isBefore(selection.anchor, selection.focus) ? selection.anchor : selection.focus;
						const focus = Point.isAfter(selection.anchor, selection.focus) ? selection.anchor : selection.focus;
						const path = anchor.path;
						const rootNode = editor.children[path[0]];
						const wholeDocSelected = Point.equals(anchor, SlateEditor.start(editor, [])) && Point.equals(focus, SlateEditor.end(editor, []));

						if (wholeDocSelected) {
							console.log("#@# wholeDocSelected", wholeDocSelected);
							event.preventDefault();
							const allNodes = editor.children;
							allNodes.forEach((node, index) => {
								Transforms.removeNodes(editor, { at: [0] });
								if (index === allNodes.length - 1) {
									Transforms.insertNodes(editor, { type: 'text', children: [{ text: '' }] }, { at: [0] });
								}
							});
						} else {
							if (rootNode.type === 'footnote-wrapper') {
								const sameStartAndEnd = Point.equals(anchor, focus);
								const start = SlateEditor.start(editor, [path[0]]);
								const end = SlateEditor.end(editor, [path[0]]);
								const isAtStart = Point.equals(anchor, start);
								const isAtEnd = Point.equals(focus, end);

								if (sameStartAndEnd && isAtStart) {
									event.preventDefault();
								} else if (event.key === 'Delete' && sameStartAndEnd && isAtEnd) {
									event.preventDefault();
								}
							} else if (anchorNode.superscript) {
								event.preventDefault();
								const end = SlateEditor.end(editor, anchorNodePath);
								const correctedSelection = {
									anchor: { path: anchorNodePath, offset: 0 },
									focus: { path: anchorNodePath, offset: end.offset }
								};
								removeFootnote(editor, anchorNode, correctedSelection);
							} else {
								const nextNode = editor.children[path[0] + (event.key === 'Backspace' ? (path[0] === 0 ? 0 : -1) : 1)];
								const nextPath = [path[0] + (event.key === 'Backspace' ? (path[0] === 0 ? 0 : -1) : 1)];

								if (nextNode && nextNode.type === 'footnote-wrapper') {
									const start = SlateEditor.start(editor, [anchor.path[0]]);
									const end = SlateEditor.end(editor, [focus.path[0]]);
									const isAtStart = Point.equals(anchor, start);
									const isAtEnd = Point.equals(focus, end);

									if ((event.key === 'Backspace' && isAtStart) || (event.key === 'Delete' && isAtEnd)) {
										const specialNode = ['table-wrapper', 'image-wrapper', 'side-by-side'];
										if (specialNode.includes(rootNode.type)) {
											event.preventDefault();
											return;
										} else {
											event.preventDefault();
											Transforms.moveNodes(editor, {
												at: event.key === 'Backspace' ? [path[0]] : [nextPath[0] + 1],
												to: [nextPath[0]],
											});
											Transforms.mergeNodes(editor, { at: [nextPath[0]] });
										}
									}
								}
							}
						}
					}

					if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
						const anchor = Point.isBefore(selection.anchor, selection.focus) ? selection.anchor : selection.focus;
						const path = anchor.path;
						const currentNode = editor.children[path[0]];

						if (currentNode.type === 'footnote-wrapper') {
							const currentOffSet = selection.anchor.offset;
							const footNoteTextElement = SlateNode.get(editor, path);
							const footNoteContentPath = [path[0], path[1]];
							const footNoteContentElement = SlateNode.get(editor, footNoteContentPath);
							const superscriptText = footNoteContentElement.superscriptNumber;
							const footnoteElement = ReactEditor.toDOMNode(editor, footNoteContentElement);
							const footnoteTextElement = ReactEditor.toDOMNode(editor, footNoteTextElement);
							const footnoteWidth = footnoteElement.getBoundingClientRect().width;
							const footnoteTextWidth = footnoteTextElement.getBoundingClientRect().width;
							const windowSelection = window.getSelection();

							const flowStyles = props.flowingConfig.styles;
							const styles = props.styles;
							const styleName = flowStyles['footnoteContent'];
							const fnContentStyle = styles[styleName] ? styles[styleName] : {};
							const fncStyle = props.data.getStyle(fnContentStyle);
							const superscriptWidth = getTextWidth(superscriptText, { ...fncStyle, fontSize: '66%' });

							if (event.key === 'ArrowUp') {
								const newOffset = windowSelection.focusOffset;
								const text = windowSelection.focusNode.textContent.slice(0, newOffset);
								const textWidth = getTextWidth(text, fncStyle);
								if ((((textWidth + superscriptWidth) < footnoteWidth) || currentOffSet === 0)) {
									event.preventDefault();
									Transforms.select(editor, {
										anchor: { ...selection.anchor, offset: 0 },
										focus: { ...selection.focus, offset: 0 }
									});
								}
							} else {
								const newOffset = windowSelection.focusOffset;
								const lastCharacter = windowSelection.focusNode.textContent.length;
								const text = windowSelection.focusNode.textContent.slice(newOffset, lastCharacter);
								const textWidth = getTextWidth(text, fncStyle);
								if (event.key === 'ArrowDown' && (lastCharacter === selection.anchor.offset || textWidth < footnoteTextWidth)) {
									event.preventDefault();
									Transforms.select(editor, {
										anchor: { ...selection.anchor, offset: lastCharacter },
										focus: { ...selection.focus, offset: lastCharacter }
									});
								}
							}
						} else {
							const nextNode = editor.children[path[0] + (event.key === 'ArrowUp' ? (path[0] === 0 ? 0 : -1) : 1)];
							const nextPath = [path[0] + (event.key === 'ArrowUp' ? (path[0] === 0 ? 0 : -1) : 1)];

							if (nextNode && nextNode.type === 'footnote-wrapper') {
								event.preventDefault();
								const nodeAfterFootnoteSelection = event.key === 'ArrowUp' ? SlateEditor.end(editor, [nextPath[0] - 1]) : SlateEditor.start(editor, [nextPath[0] + 1]);
								Transforms.select(editor, {
									anchor: nodeAfterFootnoteSelection,
									focus: nodeAfterFootnoteSelection,
								});
							}
						}
					}

					if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
						const anchor = Point.isBefore(selection.anchor, selection.focus) ? selection.anchor : selection.focus;
						const focus = Point.isAfter(selection.anchor, selection.focus) ? selection.anchor : selection.focus;
						const path = anchor.path;
						const currentNode = editor.children[path[0]];

						if (currentNode.type === 'footnote-wrapper') {
							if (event.key === 'ArrowLeft') {
								const footnoteContentPath = SlatePath.parent(path);
								const startOfFootnoteContent = SlateEditor.start(editor, footnoteContentPath);
								const isAtStart = Point.equals(anchor, startOfFootnoteContent);
								if (isAtStart) event.preventDefault();
							}
							if (event.key === 'ArrowRight') {
								const footnoteContentPath = SlatePath.parent(path);
								const endOfFootnoteContent = SlateEditor.end(editor, footnoteContentPath);
								const isAtEnd = Point.equals(focus, endOfFootnoteContent);
								if (isAtEnd) event.preventDefault();
							}
						} else {
							const nextNode = editor.children[path[0] + (event.key === 'ArrowLeft' ? (path[0] === 0 ? 0 : -1) : 1)];
							const nextPath = [path[0] + (event.key === 'ArrowLeft' ? (path[0] === 0 ? 0 : -1) : 1)];
							if (nextNode && nextNode.type === 'footnote-wrapper') {
								const start = SlateEditor.start(editor, [path[0]]);
								const end = SlateEditor.end(editor, [path[0]]);
								const isAtStart = Point.equals(anchor, start) && event.key === 'ArrowLeft';
								const isAtEnd = Point.equals(focus, end) && event.key === 'ArrowRight';

								if (isAtStart || isAtEnd) {
									event.preventDefault();
									if (isAtStart || (isAtEnd && nextPath[0] < editor.children.length - 1)) {
										const nodeAfterFootnoteSelection = event.key === 'ArrowLeft' ? SlateEditor.end(editor, [nextPath[0] - 1]) : SlateEditor.start(editor, [nextPath[0] + 1]);
										Transforms.select(editor, {
											anchor: nodeAfterFootnoteSelection,
											focus: nodeAfterFootnoteSelection,
										});
									}
								}
							}
						}
					}

					if (event.key === 'Enter') {
						if (!selection) return;

						const anchor = Point.isBefore(selection.anchor, selection.focus) ? selection.anchor : selection.focus;
						const path = anchor.path;
						const currentNode = editor.children[path[0]];

						if (currentNode.type === 'footnote-wrapper') {
							event.preventDefault();
							const footnoteContentPath = SlatePath.parent(path);
							const footnoteContentNode = SlateNode.get(editor, footnoteContentPath);
							const updatedChildren = footnoteContentNode.children.concat([{ text: '\n' }]);
							const newFootnoteContent = { ...footnoteContentNode, children: updatedChildren };
							Transforms.removeNodes(editor, { at: footnoteContentPath });
							Transforms.insertNodes(editor, newFootnoteContent, { at: footnoteContentPath });
							const endPoint = SlateEditor.end(editor, footnoteContentPath);
							Transforms.select(editor, {
								anchor: endPoint,
								focus: endPoint,
							});
							return;
						}

						const anchorNodeEl = window.getSelection().anchorNode;
						const anchorNode = ReactEditor.toSlateNode(editor, anchorNodeEl);
						const anchorNodePath = ReactEditor.findPath(editor, anchorNode);

						if (anchorNode.superscript) {
							event.preventDefault();
							Transforms.insertNodes(editor, { type: 'text', children: [{ text: '' }] }, { at: [anchorNodePath[0] + 1] });
							Transforms.select(editor, { anchor: { path: [anchorNodePath[0] + 1, 0], offset: 0 }, focus: { path: [anchorNodePath[0] + 1, 0], offset: 0 } });
						}
					}
				},
				editor: editor
			};
		}
	};

	const handleSlateExtensions = (extensionsConfig) => buildSlateExtensions({
		extensionsConfig,
		getPortfoliosState: () => props.portfoliosState,
		getPortfoliosGroupedState: () => props.portfoliosGroupedState,
		getCurrentFundNumber: () => props.currentFundNumber,
		getPasteGroupings: () => props.pasteGroupings,
		getAllFunds: () => props.allFunds,
		pasteGroupName: props.flowingConfig.pasteGroup,
		remote: props.remote,
		getContent: () => state.content,
		templateName: props.data.templateName,
		dataName: props.flowingConfig.dataName,
		keepInsertOpen: state.keepInsertOpen,
		setKeepInsertOpen: (boolean) => setState({ keepInsertOpen: boolean }),
		onInsertPageBreak: (selection) => {
			insertPageBreak(selection, state, props.data, setState, heightChange, height);
		},
	});

	const sbs = sidebysides(tableOptions, imageOptions, sbsOptions);
	const addins = simpleInlineStyles().concat([headings, lists, sbs, editorRef, pageBreaksAddin, tableOfContents, pageFootnoteWrapper]);

	return { addins, options, myStyles, handleSlateExtensions };
}
